import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowRight, Sofa, UtensilsCrossed, Sparkles, Heart, Truck, HandHeart } from "lucide-react";
import { FadeUp, MaskedLine, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const HERO_IMG = "https://images.unsplash.com/photo-1651407825801-eec2dcbd86bd?w=1600&q=80&auto=format&fit=crop";
const FAMILY_IMG = "https://images.unsplash.com/photo-1758687125973-ec9932a4245b?w=1200&q=80&auto=format&fit=crop";
const COZY_IMG = "https://images.unsplash.com/photo-1641232458416-feace752b346?w=1200&q=80&auto=format&fit=crop";

const STEPS = [
  {
    n: "01",
    title: "Community Donates",
    text: "Neighbors donate gently used furniture, household essentials, home décor, or monetary support.",
    icon: HandHeart,
  },
  {
    n: "02",
    title: "We Collect & Prepare",
    text: "Fresh Start Furnishings coordinates donation pickup, drop-off, storage, and preparation.",
    icon: Truck,
  },
  {
    n: "03",
    title: "Families Shop Affordably",
    text: "Women and families rebuilding their homes access available items at deeply discounted prices.",
    icon: Sofa,
  },
];

const CATEGORIES = [
  { icon: Sofa, title: "Furniture", items: ["Couches & loveseats", "Dining tables & chairs", "Beds & dressers", "Desks & bookshelves"] },
  { icon: UtensilsCrossed, title: "Household Essentials", items: ["Pots, pans & dishes", "Small kitchen appliances", "Bedding, sheets & towels", "Storage & laundry"] },
  { icon: Sparkles, title: "Home Décor", items: ["Rugs & curtains", "Mirrors & wall décor", "Vases & picture frames", "Plants & planters"] },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const glowOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pb-20 pt-32 sm:pt-40">
        <motion.div
          style={{ opacity: glowOpacity }}
          className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-burgundy/10 blur-3xl"
        />
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <FadeUp>
              <Overline>A Community Pilot Program — Glendale, Arizona</Overline>
            </FadeUp>
            <h1 data-testid="hero-headline" className="mt-5 font-serif text-4xl leading-[1.06] tracking-tight text-olive sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>TURNING EMPTY</MaskedLine>
              <MaskedLine delay={0.28}>HOUSES INTO</MaskedLine>
              <MaskedLine delay={0.41} className="italic text-burgundy">HOMES.</MaskedLine>
            </h1>
            <FadeUp delay={0.55} className="mt-6 max-w-xl text-base leading-relaxed text-moss sm:text-lg">
              <strong className="text-ink">Fresh Start Furnishings</strong> helps women and families rebuilding their
              lives access donated furniture and household essentials at deeply affordable prices.
            </FadeUp>
            <FadeUp delay={0.65}>
              <p data-testid="hero-service-area" className="mt-5 inline-flex items-center gap-2 rounded-full border border-linen bg-beige px-4 py-2 text-sm font-medium text-olive">
                <MapPin className="h-4 w-4 text-burgundy" /> Serving Glendale, the West Valley &amp; Surrounding Areas
              </p>
            </FadeUp>
            <FadeUp delay={0.75} className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link to="/donate-items" data-testid="hero-donate-items-button" className="btn-accent">
                Donate Items <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/shop" data-testid="hero-shop-button" className="btn-primary">
                Shop Affordable Furniture
              </Link>
              <Link to="/support" data-testid="hero-support-button" className="btn-outline">
                Support the Pilot Program
              </Link>
            </FadeUp>
          </div>
          <motion.div style={{ y: imgY }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-3xl border border-linen bg-beige p-3 shadow-xl shadow-olive/10"
            >
              <img
                src={HERO_IMG}
                alt="A warm, furnished living room"
                className="aspect-[4/5] w-full rounded-2xl object-cover sm:aspect-[5/6]"
              />
              <div className="absolute -bottom-6 -left-4 rounded-2xl border border-linen bg-cream px-6 py-4 shadow-lg sm:-left-10">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-burgundy">Affordability Promise</p>
                <p className="mt-1 font-serif text-2xl text-olive">
                  Furnish a home for <span className="italic text-burgundy">under $500</span>
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="how-it-works-preview">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <FadeUp><Overline>How It Works</Overline></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-3 font-serif text-3xl tracking-tight text-olive sm:text-4xl">
                Three steps from donation <span className="italic text-burgundy">to dignity.</span>
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.2}>
            <Link to="/how-it-works" data-testid="how-it-works-link" className="btn-outline !py-2.5 text-xs">
              See the full process <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <FadeUp key={s.n} delay={i * 0.12}>
              <div data-testid={`how-step-${i + 1}`} className="bento-card group h-full">
                <div className="flex items-center justify-between">
                  <span className="font-serif text-5xl italic text-burgundy/40 transition-colors duration-300 group-hover:text-burgundy">{s.n}</span>
                  <s.icon className="h-7 w-7 text-olive" />
                </div>
                <h3 className="mt-5 font-serif text-xl text-olive sm:text-2xl">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-moss">{s.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2}>
          <p data-testid="reinvested-statement" className="mt-12 border-l-2 border-burgundy pl-6 font-serif text-xl italic leading-relaxed text-olive sm:text-2xl">
            Donated by the community. Offered at affordable prices. Reinvested into helping the next family start over.
          </p>
        </FadeUp>
      </section>

      {/* WHAT WE ACCEPT PREVIEW */}
      <section className="border-y border-linen bg-beige/60 py-24" data-testid="what-we-accept-preview">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <FadeUp><Overline>What We Accept</Overline></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-3 max-w-2xl font-serif text-3xl tracking-tight text-olive sm:text-4xl">
              Your gently used items become <span className="italic text-burgundy">someone's fresh start.</span>
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {CATEGORIES.map((c, i) => (
              <FadeUp key={c.title} delay={i * 0.12}>
                <div data-testid={`accept-category-${c.title.toLowerCase().replace(/\s+/g, "-")}`} className="bento-card h-full bg-cream">
                  <c.icon className="h-8 w-8 text-burgundy" />
                  <h3 className="mt-4 font-serif text-2xl text-olive">{c.title}</h3>
                  <ul className="mt-4 space-y-2">
                    {c.items.map((it) => (
                      <li key={it} className="flex items-center gap-2 text-sm text-moss">
                        <Heart className="h-3 w-3 shrink-0 fill-burgundy/20 text-burgundy" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.25} className="mt-10 flex flex-wrap gap-3">
            <Link to="/what-we-accept" data-testid="see-all-accepted-link" className="btn-outline !py-2.5 text-xs">
              See everything we accept
            </Link>
            <Link to="/donate-items" data-testid="preview-start-donation-button" className="btn-accent !py-2.5 text-xs">
              Start a Donation <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* AFFORDABILITY PROMISE */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="affordability-section">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <FadeUp>
            <div className="relative">
              <img src={FAMILY_IMG} alt="Family relaxing on their couch at home" className="aspect-[4/3] w-full rounded-3xl border border-linen object-cover shadow-xl shadow-olive/10" />
              <img src={COZY_IMG} alt="A cozy furnished corner" className="absolute -bottom-8 -right-4 hidden w-44 rounded-2xl border-4 border-cream object-cover shadow-lg sm:block" />
            </div>
          </FadeUp>
          <div>
            <FadeUp><Overline>Our Affordability Promise</Overline></FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-4 font-serif text-6xl tracking-tight text-olive sm:text-7xl">
                <span className="italic text-burgundy">$500</span> <span className="text-2xl italic text-sage sm:text-3xl">or less</span>
              </p>
            </FadeUp>
            <FadeUp delay={0.2} className="mt-5 space-y-4 text-base leading-relaxed text-moss">
              <p>
                Our goal is that a family will <strong className="text-ink">not pay more than $500 to help furnish an
                entire home</strong> — based on household needs, available inventory, and program guidelines.
              </p>
              <p>
                Individual items are priced at low, affordable prices — and thanks to generous community donations,
                we're sometimes able to provide furniture to families <strong className="text-ink">completely free of
                charge</strong>, depending on available funding and household circumstances.
              </p>
            </FadeUp>
            <FadeUp delay={0.3} className="mt-7 flex flex-wrap gap-3">
              <Link to="/shop" data-testid="promise-learn-more-button" className="btn-primary">How Shopping Works</Link>
              <Link to="/apply-to-shop" data-testid="promise-apply-button" className="btn-accent">Apply to Shop</Link>
            </FadeUp>
          </div>
        </div>
      </section>

      <Marquee dark={false} />

      {/* FINAL CTA */}
      <section className="bg-burgundy py-24 text-cream" data-testid="final-cta-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <FadeUp>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Three Ways to Help</p>
            <h2 data-testid="final-cta-headline" className="mt-4 font-serif text-3xl tracking-tight sm:text-5xl">
              YOU CAN HELP CREATE <span className="italic text-gold">A FRESH START.</span>
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { icon: Sofa, title: "Donate Furniture", text: "Give your gently used furniture a second purpose.", testid: "final-donate-furniture" },
              { icon: UtensilsCrossed, title: "Donate Household Essentials", text: "Help fill a home with the things families need every day.", testid: "final-donate-essentials" },
              { icon: Heart, title: "Make a Monetary Donation", text: "Help us cover pickups, storage, transportation, and delivery.", testid: "final-donate-funds" },
            ].map((c, i) => (
              <FadeUp key={c.title} delay={i * 0.12}>
                <div data-testid={c.testid} className="h-full rounded-2xl border border-cream/20 bg-burgundy-deep p-8 transition-colors duration-300 hover:border-gold/50">
                  <c.icon className="h-8 w-8 text-gold" />
                  <h3 className="mt-4 font-serif text-2xl">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-cream/70">{c.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.25} className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Link to="/donate-items" data-testid="final-donate-items-button" className="btn-outline-light">Donate Items</Link>
            <Link to="/apply-to-shop" data-testid="final-apply-button" className="btn-outline-light">Apply to Shop</Link>
            <Link to="/support" data-testid="final-support-button" className="btn-outline-light">Support the Pilot Program</Link>
          </FadeUp>
          <FadeUp delay={0.35} className="mt-20 border-t border-cream/15 pt-14 text-center">
            <p className="font-serif text-2xl leading-snug tracking-tight sm:text-4xl">
              DONATE FURNITURE. DONATE ESSENTIALS. DONATE FUNDS.
            </p>
            <p className="mt-4 font-script text-3xl text-gold sm:text-4xl">Together, we can turn empty houses into homes.</p>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
